import invariant from 'invariant'
import {pickBy, isEmpty} from 'lodash-es'
import {API} from '../../../constants/routes'
import generatePath from './generatePath'
import type {Parameters} from './generatePath'

export type Options = {
  data?: Record<string, unknown>
  query?: Record<string, string>
  params?: Parameters
  sessionToken?: string
  signal?: AbortSignal
  asBlob?: boolean
  asMultipart?: boolean
}

export type HttpMethod = 'DELETE' | 'GET' | 'POST' | 'PUT'

export type ErrorResponse = {
  status: number
  statusText: string
  errorCode?: string
  message: string
}

export class FrontendError extends Error {
  data?: Partial<ErrorResponse>
}

export const getQueryString = (query?: Record<string, string>) => {
  return !isEmpty(query) ? `?${new URLSearchParams(query).toString()}` : ''
}

export const api = async <R>(method: HttpMethod, pathPattern: string, options?: Options): Promise<R> => {
  invariant(typeof window !== 'undefined', 'Web API calls are allowed only in browser')

  const {data, query, params} = options || {}
  const path = generatePath(pathPattern, params)
  const search = getQueryString(query)
  const hasBody = !['HEAD', 'GET'].includes(method.toUpperCase())
  const headers = pickBy({
    'Content-type': hasBody ? 'application/json' : undefined,
  }) as HeadersInit

  return window // eslint-disable-next-line no-process-env
    .fetch(`${process.env.GATSBY_API_URL}${API}${path}${search}`, {
      method,
      headers,
      body: hasBody ? JSON.stringify(data || {}) : undefined,
      credentials: 'include',
    })
    .then((res) => {
      const contentType = res.headers.get('content-type') || ''
      if (contentType.indexOf('application/json') > -1) {
        return res.json().then((data) => ({res, data}))
      } else {
        return res.text().then((data) => ({res, data}))
      }
    })
    .then(({res, data}) => {
      if (res.ok) {
        return data as R
      } else {
        const error = new FrontendError(data?.error?.message || res.statusText || 'Unknown error')
        error.data = data as ErrorResponse
        throw error
      }
    })
}
